import { useState } from 'react';
import { IconAlertTriangle, IconArrowLeft, IconCalendar, IconClick, IconDots, IconLoader, IconSearch, IconSparkles, IconX } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { useNavigate } from '@tanstack/react-router';
import { Page, PageHeader, useShell } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { HOURS, LANES, PENDING, assign, countAssigned, hourLabel, slotBox, slotLabel, slotTitle, unassign, type Lane, type Pending } from './dispatch';
import { useT } from '@hana/i18n';
import './i18n';
import { useWide } from './useWide';
import s from './dispatch.module.css';

// Mint-TMS-Dispatch (desktop 1536) · tab "Lưới điều xe". Chọn lệnh chờ → bấm tên xe để gán (giả lập, giữ trong bộ nhớ, toast có Hoàn tác).
// Chưa làm: các tab Bản đồ mooc & đầu kéo / Ngoại lệ / Dự báo nhu cầu xe / Ghép VETC & cảng, kéo thả thanh, ngày mai có dữ liệu.

type Tab = 'grid' | 'map' | 'exc' | 'fcst' | 'vetc';
const TABS: ReadonlyArray<{ key: Tab; label: string; n?: number }> = [
  { key: 'grid', label: 'Lưới điều xe', n: 3 },
  { key: 'map', label: 'Bản đồ mooc & đầu kéo' },
  { key: 'exc', label: 'Ngoại lệ', n: 5 },
  { key: 'fcst', label: 'Dự báo nhu cầu xe' },
  { key: 'vetc', label: 'Ghép VETC & cảng', n: 2 },
];

// Bảng phải "Ngoại lệ" (Mint-TMS-Dispatch-1920: dock 372, mở sẵn ≥ 1800; nhỏ hơn mở bằng nút đầu trang). Dữ liệu theo EXC của board.
const EXC: ReadonlyArray<{ id: string; k: string; v: string; ak: string; tone: 'err' | 'warn' }> = [
  { id: 'e1', k: 'Mooc lưu lâu', v: '50RM05007 lưu Kho lạnh NECS 52 giờ', ak: 'Tính phí lưu', tone: 'err' },
  { id: 'e2', k: 'Chặng GPS chờ xác nhận', v: 'TRF2610078 · 2 chặng (neo xe, có hàng)', ak: 'Mở lệnh', tone: 'warn' },
  { id: 'e3', k: 'Lệnh trễ', v: 'TRF2610028 · QUYNHVY lấy rỗng trễ 40′ so hẹn 03:00', ak: 'Gọi nhà xe', tone: 'warn' },
  { id: 'e4', k: 'Xe khoá kỹ thuật', v: '50H60625 · quá hạn BD2 3.330 km', ak: 'Mở xe', tone: 'err' },
  { id: 'e5', k: 'Quá hạn Finish', v: '50E32294 · TRF2610085 và 1 lệnh khác · đã bỏ nhắc 3 lần', ak: 'Nhắc Finish', tone: 'err' },
];

function ExcDock({ done, onDone, onClose }: { done: ReadonlySet<string>; onDone: (id: string) => void; onClose: () => void }) {
  const t = useT();
  return (
    <div className={s.dock} aria-label={t('tms.exceptions')}>
      <div className={s.dkHd}>
        <b>Ngoại lệ</b>
        <button type="button" className={s.dkX} aria-label="Đóng bảng" onClick={onClose}><IconX size={17} aria-hidden="true" /></button>
      </div>
      {EXC.map((e) => (
        <div key={e.id} className={s.alert}>
          <span className={s.alDot} style={{ background: done.has(e.id) ? 'var(--ok)' : e.tone === 'err' ? 'var(--err)' : 'var(--d-amber)' }} />
          <div className={s.alBody}><div style={{ fontWeight: 550 }}>{e.k}</div><div className={s.alV}>{done.has(e.id) ? 'Đã xử lý' : e.v}</div></div>
          {!done.has(e.id) && <button type="button" className={s.alAct} onClick={() => onDone(e.id)}>{e.ak}</button>}
        </div>
      ))}
    </div>
  );
}

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const KPIS: ReadonlyArray<{ key: string; label: string; value: string; unit: string; tone: '' | 'warn' | 'err' | 'ok'; icon?: boolean }> = [
  { key: 'plan', label: 'Chưa gán (NEED-PLAN)', value: '3', unit: 'lệnh', tone: 'warn', icon: true },
  { key: 'run', label: 'Đang chạy', value: '11', unit: 'Processing', tone: '' },
  { key: 'moc', label: 'Mooc lưu > 24 giờ', value: '1', unit: '50RM05007 · 52 giờ', tone: 'warn' },
  { key: 'late', label: 'Quá hạn Finish', value: '2', unit: 'xe 50E32294', tone: 'err' },
  { key: 'done', label: 'Đã Finish hôm nay', value: '4', unit: 'JobStatus Finish', tone: 'ok' },
];

function Grid() {
  const toast = useToast();
  const t = useT();
  const navigate = useNavigate();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const [lanes, setLanes] = useState<readonly Lane[]>(LANES);
  const [pending, setPending] = useState<readonly Pending[]>(PENDING);
  const [pick, setPick] = useState<string | null>(null);
  const [day, setDay] = useState<'today' | 'tomorrow'>('today');
  const shown = day === 'today' ? lanes : lanes.map((l) => ({ ...l, slots: [] }));
  const own = lanes.filter((l) => !l.outside).length;

  const pickLane = (name: string) => {
    const order = pending.find((p) => p.id === pick);
    if (!order) return toast({ msg: 'Chọn một lệnh chờ gán trước, rồi bấm tên xe' });
    if (day !== 'today') return toast({ msg: 'Chưa mở gán cho ngày mai ở bản này' });
    const next = assign(lanes, name, order);
    if (!next) return toast({ msg: `${name} đang khoá kỹ thuật, không gán được`, tone: 'err' });
    const prevLanes = lanes;
    const prevPending = pending;
    setLanes(next);
    setPending(pending.filter((p) => p.id !== order.id));
    setPick(null);
    toast({ msg: `Đã gán ${order.id} cho ${name}`, undo: () => { setLanes(unassign(prevLanes, order.id)); setPending(prevPending); } });
  };

  return (
    <>
      <div className={s.kpis}>
        {KPIS.map((k) => (
          <button key={k.key} type="button" className={s.kpi} aria-label={`${k.label} ${k.key === 'plan' ? pending.length : k.value} ${k.unit}`} onClick={noWrite}>
            <div className={s.kpiL}>
              {k.icon && <IconLoader size={16} stroke={1.9} aria-hidden="true" />}
              <span>{k.label}</span>
            </div>
            <div className={s.kpiV}>
              <b className={k.tone ? s[k.tone] : ''}>{k.key === 'plan' ? pending.length : k.value}</b>
              <span>{k.unit}</span>
            </div>
          </button>
        ))}
      </div>
      <div className={s.body}>
        <section aria-label="Lệnh chờ gán" className={`${s.card} ${s.panel}`}>
          <div className={s.pHd}>
            <h2 className={s.h2}>{t('tms.pending')}</h2>
            <span className={s.hint}>{t('tms.pendingHint')}</span>
          </div>
          {pending.map((p) => (
            <div key={p.id} className={s.pRow}>
              <span className={s.dot} />
              <div className={s.pTx}>
                <b>{`${p.id} · ${p.customer}`}</b>
                <div className={s.pSub}>{p.desc}</div>
              </div>
              <button type="button" className={`${s.pick} ${pick === p.id ? s.pickOn : ''}`} aria-pressed={pick === p.id} onClick={() => setPick(pick === p.id ? null : p.id)}>
                <IconClick size={16} stroke={1.8} aria-hidden="true" />
                {t('tms.pick')}
              </button>
            </div>
          ))}
        </section>
        <section aria-label={`Lưới điều xe · ${day === 'today' ? '23/10/2026' : '24/10/2026'}`} className={`${s.card} ${s.grid}`}>
          <div className={s.gHd}>
            <h2 className={s.h2}>{`Lưới điều xe · ${day === 'today' ? '23/10/2026' : '24/10/2026'}`}</h2>
            <div className={s.days}>
              <button type="button" className={`${s.day} ${day === 'today' ? s.dayOn : ''}`} aria-pressed={day === 'today'} onClick={() => setDay('today')}>
                <IconCalendar size={16} stroke={1.9} aria-hidden="true" />{t('tms.today')}
              </button>
              <button type="button" className={`${s.day} ${day === 'tomorrow' ? s.dayOn : ''}`} aria-pressed={day === 'tomorrow'} onClick={() => setDay('tomorrow')}>
                <IconCalendar size={16} stroke={1.9} aria-hidden="true" />{t('tms.tomorrow')}
              </button>
            </div>
          </div>
          <div className={s.ticks} aria-hidden="true">
            {HOURS.map((h) => (
              <span key={h}><span>{hourLabel(h)}</span></span>
            ))}
          </div>
          {shown.map((l) => (
            <div key={l.name} className={`${s.lane} ${l.warn ? s.laneWarn : ''}`}>
              <button type="button" className={`${s.name} ${l.outside ? s.nameOut : ''}`} aria-label={l.name} onClick={() => pickLane(l.name)}>
                <b>{l.name}</b>
                <div className={`${s.nameSub} ${l.warn ? s.warnTx : ''}`}>{l.sub}</div>
              </button>
              <div className={s.track}>
                {l.slots.map((x) => {
                  const b = slotBox(x);
                  return (
                    <a
                      key={x.id}
                      className={s.bar}
                      href={`/ezy/tms/trips/${x.id}`}
                      title={slotTitle(x)}
                      style={{ left: `${b.left}%`, width: `calc(${b.width}% - 3px)` }}
                      onClick={(e) => { e.preventDefault(); void navigate({ to: `/ezy/tms/trips/${x.id}` }); }}
                    >
                      {slotLabel(x)}
                    </a>
                  );
                })}
              </div>
            </div>
          ))}
          <div className={s.foot}>
            {`${own} đầu kéo xe nhà (HanaLog) · ${lanes.length - own} nhà xe ngoài hay dùng · điều xe PhongNT`}
          </div>
        </section>
      </div>
    </>
  );
}

export function DispatchPage() {
  const toast = useToast();
  const t = useT();
  const navigate = useNavigate();
  const { openFind } = useShell();
  const { data, error, isLoading, isError, refetch } = useList('trip', { limit: 500 });
  const forced = readState();
  const [tab, setTab] = useState<Tab>('grid');
  const wide = useWide();
  const [dockOn, setDockOn] = useState(false);
  const [dockX, setDockX] = useState(false);
  const [excDone, setExcDone] = useState<ReadonlySet<string>>(new Set());
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if ((data?.items.length ?? 0) === 0) state = 'empty';

  const dockOpen = tab === 'grid' && state === 'ready' && (wide ? !dockX : dockOn);
  const closeDock = () => (wide ? setDockX(true) : setDockOn(false));
  const doExc = (id: string) => {
    const e = EXC.find((x) => x.id === id);
    setExcDone((d) => new Set(d).add(id));
    toast({ msg: `${e?.ak ?? ''} · ${(e?.v ?? '').split(' · ')[0]}`, undo: () => setExcDone((d) => { const n = new Set(d); n.delete(id); return n; }) });
  };

  return (
    <Page kind="table" dock={dockOpen ? <ExcDock done={excDone} onDone={doExc} onClose={closeDock} /> : undefined}>
      <PageHeader
        title="Lưới điều xe"
        noFind
        lead={
          <button type="button" className={s.back} aria-label="Về Vận tải" title="Về Vận tải" onClick={() => void navigate({ to: '/ezy/tms' })}>
            <IconArrowLeft size={18} aria-hidden="true" />
          </button>
        }
        tabs={
          <span className={s.hChips}>
            <span className={`${s.hChip} ${s.hBlue}`}><i style={{ background: 'var(--d-blue)' }} />{t('tms.onDuty')}</span>
            <span className={`${s.hChip} ${s.hWarn}`}><i style={{ background: 'var(--d-amber)' }} />{`${PENDING.length} lệnh chưa gán`}</span>
          </span>
        }
        actions={
          <>
            <Segmented kind="toggle" label="Mật độ" value="std" onChange={noWrite} items={[{ key: 'std', label: 'Chuẩn' }, { key: 'compact', label: 'Gọn' }]} />
            <button type="button" className={s.more} aria-label="Tìm nhanh (Ctrl K)" title="Tìm nhanh (Ctrl K)" onClick={openFind}>
              <IconSearch size={18} aria-hidden="true" />
            </button>
            {!(wide && dockOpen) && (
              <Button variant="secondary" icon={<IconAlertTriangle size={16} />} onClick={() => (wide ? setDockX(false) : setDockOn(!dockOn))}>{t('tms.exceptions')}</Button>
            )}
            <Button variant="ask" icon={<IconSparkles size={16} />} onClick={noWrite}>{t('tms.ask')}</Button>
            <button type="button" className={s.more} aria-label="Thêm thao tác" title="Thêm thao tác" onClick={noWrite}>
              <IconDots size={18} aria-hidden="true" />
            </button>
          </>
        }
      />
      <div className={s.page}>
        <div className={s.sub}>{`Ngày 23/10/2026 · ${countAssigned(LANES)} lệnh đã gán`}</div>
        <div className={s.tabs}>
          <Segmented label="Mục lưới điều xe" value={tab} onChange={setTab} items={TABS.map((t) => ({ key: t.key, label: t.label, n: t.n }))} />
        </div>
        <StateView
          state={tab === 'grid' ? state : 'empty'}
          title={tab !== 'grid' ? 'Mục này chưa mở' : state === 'empty' ? 'Chưa có lệnh để điều xe' : undefined}
          action={state === 'error' ? <Button variant="secondary" onClick={() => void refetch()}>{t('common.retry')}</Button> : undefined}
          skeleton={
            <>
              <div className={s.kpis}>{[1, 2, 3, 4, 5].map((k) => <div key={k} className={s.skel} style={{ height: 87 }} />)}</div>
              <div className={s.skel} style={{ marginTop: 14, padding: 18, height: 380 }}><Skeleton /></div>
            </>
          }
        >
          <Grid />
        </StateView>
      </div>
    </Page>
  );
}
